import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  AbilityEffect,
  SummonOption2,
  CharacterSheetInterface,
  CharacterSpell,
  Attack,
  ExtraEffectsFeature,
  CharacterFeature,
  ExtraEffectsOfTypeFeature,
  CharacterAction,
  CharacterExtraEffects,
  StoredSpell,
} from "@dndModels";
import { DamageType, SpellLevel } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import DataUtilities from '../../../utilities/common_utilities';

import DandDButtonBox from "@dndComponents/DandDButtonBox";

import CharacterSummonOptions from "@dndComponents/character/misc/CharacterSummonOptions";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import LoadingBox from "@components/input/LoadingBox";
import DisplaySummonOption from "@dndComponents/display/DisplaySummonOption";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  action: CharacterAction;
  effect: AbilityEffect;
  cee?: CharacterExtraEffects;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  ee_cf?: CharacterFeature;
  type_cf?: CharacterFeature;
  shared: boolean;
  level: SpellLevel;
  wide: boolean;
  show_extra_effects?: boolean;
  attack_mod?: number;
  main_damage_type?: DamageType;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onChange: (change_types: string[]) => void; // For slots, resources, and concentration
  onCostChange: (costs: { [resource: string]: number }) => void;
  hide_touch?: boolean;
  extra_string: string;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

export default function CharacterSummonEffect({
  character,
  char_interface,
  spell,
  action,
  effect,
  cee,
  processing,
  disabled,
  ee_cf,
  shared,
  level,
  attack_mod = 0,
  main_damage_type,
  hide_touch = false,
  onChange,
  onCostChange,
  extra_string,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [is_processing, setProcessing] = useState(false);
  // const [ping, setPing] = useState(false);
  // const [keep_rolls, setKeepRolls] = useState<any[]>([]);
  const [summon_option_list, setSummonOptionList] = useState<SummonOption2[]>([]);
  const [selected_option, setSelectedOption] = useState<SummonOption2 | null>(null);
  // const [selected_id, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback( async () => {
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
      char_util.getPotence({ slot_level: level, char: character, item_id, char_int: char_interface, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod }) : 
      spell ? char_util.getPotence({ slot_level: level, char: character, item_id, char_int: char_interface, spell, effect, cfs_id: "", attack_mod, action }) : null;
    // let extra_string = "";
    if (potence) {
      // if (spell && spell.name.includes("Familiar")) {
      //   debugger;
      // }
      setLoading(true);
      // extra_string = potence.extra;
      const options = await char_util.getSummonOption2s(character, potence);
      setSummonOptionList(options);
      setLoading(false);
    }
  }, []);
   
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (character.owner_id === api.user._id || (character.campaign && character.campaign.owner_id === api.user._id)));
  const item_id = spell instanceof CharacterAbility ? spell.item_id : (spell instanceof CharacterSpell ? spell.source_true_id : "");

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={open}
          onClose={() => { 
            setOpen(false); 
            character.subscription_holder.modalClosed();
          }}
        >
          <>
            { open && renderSummonOptions() }
          </>
        </ModalOrDrawer>
      </>
    );
  }

  const renderSummonOptions = () => {
    const option = summon_option_list.length === 1 ? summon_option_list[0] : selected_option;
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} className="header centered">
          Summon Options
        </Grid>
        <Grid size={12}>
          <CharacterSummonOptions 
            name="Choose"
            character={character}
            value={option}
            summon_option_list={summon_option_list}
            shared={shared}
            index
            onChange={setSelectedOption}
          />
        </Grid>
        { selected_option && spell &&
          <Grid size={12}>
            <DisplaySummonOption
              character={character}
              obj={selected_option}
              spell={spell}
              level={level}
            />
          </Grid>
        }
      </Grid>
    );
  }

  const summonSelected = async (selected_option: SummonOption2) => {
    if (spell) {
      // debugger;
      setProcessing(true);
      await char_util.summonMinions({ character, spell, level, selected_option });
      setProcessing(false);
      onChange(["Minions"]);
    }
  }

  const viewSelected = () => {
    // Opens a modal with a viewable version of the statblock without actually summoning it
    setOpen(true);
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const option = summon_option_list.length === 1 ? summon_option_list[0] : selected_option;
    let range_string = spell instanceof Attack ? "Self" : char_util.getRangeString({ char_int: character, spell, effect });
    if (hide_touch && (range_string === "Touch" || range_string === "Self")) {
      range_string = "";
    }
    return (
      <Grid className="centered"
        container spacing={0} direction="row">
        <Grid size={4} className="centered">
          { range_string }{ extra_string !== "" ? (range_string !== "" ? ` / ${extra_string}` : extra_string) : "" }
        </Grid>
        <Grid size={4}>
          { summon_option_list.length !== 1 ?
            <CharacterSummonOptions 
              name="Choose"
              character={character}
              // potence={potence}
              value={option}
              summon_option_list={summon_option_list}
              shared={shared}
              onChange={setSelectedOption}
            />
          : option &&
            <>{ option.name }</>
          }
        </Grid>
        <Grid size={4}>
          <Grid container spacing={0} direction="row">
            <Grid size={{ xs: 12, sm: 6 }}>
              <DandDButtonBox 
                name="Summon"
                disabled={ !enabled || option === null || option === undefined || processing }
                onClick={() => {
                  // debugger;
                  if (option) {
                    summonSelected(option);
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DandDButtonBox 
                name="View"
                disabled={ !enabled }
                onClick={() => {
                  viewSelected();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        { renderExtras() }
      </Grid>
    );
  }
}
